import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import AcUnitIcon        from '@mui/icons-material/AcUnit';
import ArchiveIcon       from '@mui/icons-material/Archive';
import PhoneAndroidIcon  from '@mui/icons-material/PhoneAndroid';  // Local
import CloudOffIcon      from '@mui/icons-material/CloudOff';       // Unavailable

interface StorageTierBadgeProps {
  /** COOL | ARCHIVE | LOCAL | UNAVAILABLE | OFFLOADED | HOT | null */
  tier: string | null | undefined;
  sizeBytes?: number | null;
  onClick?: (e?: React.MouseEvent) => void;
}

function formatSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1_048_576).toFixed(2)} MB`;
}

function estimateCost(tier: string, bytes?: number | null): string {
  const mb = bytes ? bytes / 1_048_576 : 0;
  if (tier === 'COOL')    return `~$${(2.0 + mb * 0.10).toFixed(2)} to retrieve ($2.00 base + $0.10/MB)`;
  if (tier === 'ARCHIVE') return `~$${(7.0 + mb * 0.20).toFixed(2)} to retrieve ($7.00 base + $0.20/MB)`;
  return '';
}

interface TierConfig {
  icon:    React.ReactNode;
  label:   string;
  tooltip: string;
  bg:      string;
  fg:      string;
  border:  string;
}

function getTierConfig(tier: string, sizeBytes?: number | null): TierConfig {
  const sizeLabel = formatSize(sizeBytes);
  const costHint  = estimateCost(tier, sizeBytes);

  switch (tier) {
    case 'COOL':
      return {
        icon:    <AcUnitIcon style={{ fontSize: 14 }} />,
        label:   'Cool',
        tooltip: `❄️ Cool storage — message body not in hot cache.\n${sizeLabel ? `Size: ${sizeLabel}. ` : ''}${costHint}\nClick to retrieve.`,
        bg: '#e3f2fd', fg: '#1565c0', border: '#90caf9',
      };
    case 'ARCHIVE':
      return {
        icon:    <ArchiveIcon style={{ fontSize: 14 }} />,
        label:   'Archive',
        tooltip: `📦 Archive storage — long-term cold storage (365+ days old).\n${sizeLabel ? `Size: ${sizeLabel}. ` : ''}${costHint}\nRetrieving may take a few minutes.`,
        bg: '#fce4ec', fg: '#880e4f', border: '#f48fb1',
      };
    case 'LOCAL':
      return {
        icon:    <PhoneAndroidIcon style={{ fontSize: 14 }} />,
        label:   'Local',
        tooltip: `📱 Stored on this device — click to read from your local archive.\nDecrypted on-demand when you open it.`,
        bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7',
      };
    case 'UNAVAILABLE':
    case 'OFFLOADED':
      return {
        icon:    <CloudOffIcon style={{ fontSize: 14 }} />,
        label:   'Offline',
        tooltip: `☁️ This message was downloaded to another device.\nNot available here unless you upload your saved copy.`,
        bg: '#f5f5f5', fg: '#757575', border: '#bdbdbd',
      };
    default:
      return {
        icon:    <ArchiveIcon style={{ fontSize: 14 }} />,
        label:   tier,
        tooltip: tier,
        bg: '#f5f5f5', fg: '#424242', border: '#bdbdbd',
      };
  }
}

const StorageTierBadge: React.FC<StorageTierBadgeProps> = ({ tier, sizeBytes, onClick }) => {
  if (!tier || tier === 'HOT') return null;

  const cfg = getTierConfig(tier, sizeBytes);

  return (
    <Tooltip
      title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{cfg.tooltip}</span>}
      placement="top"
      arrow
    >
      <Chip
        size="small"
        icon={cfg.icon as React.ReactElement}
        label={cfg.label}
        onClick={onClick}
        clickable={!!onClick}
        sx={{
          height: 20,
          fontSize: 10,
          fontWeight: 600,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: cfg.bg,
          color:   cfg.fg,
          border:  `1px solid ${cfg.border}`,
          '& .MuiChip-icon': { color: cfg.fg },
          ml: 0.5,
        }}
      />
    </Tooltip>
  );
};

export default StorageTierBadge;
